const API_BASE_URL ='https://api.onurbeton.com.tr/api';

// Token storage keys
const TOKEN_KEY = 'admin_token';
const USER_KEY = 'admin_user';
const EXPIRES_KEY = 'admin_expires';

// Get stored token
export const getStoredToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

// Get stored user info
export const getStoredUser = () => {
  const userJson = localStorage.getItem(USER_KEY);
  return userJson ? JSON.parse(userJson) : null;
};

// Check if token is expired
export const isTokenExpired = () => {
  const expiresAt = localStorage.getItem(EXPIRES_KEY);
  if (!expiresAt) return true;
  
  return new Date() > new Date(expiresAt);
};

// Store authentication data
const storeAuthData = (token, username, expiresAt) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify({ username }));
  localStorage.setItem(EXPIRES_KEY, expiresAt);
};

// Clear authentication data
const clearAuthData = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(EXPIRES_KEY);
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = getStoredToken();
  const user = getStoredUser();
  
  if (!token || !user || isTokenExpired()) {
    clearAuthData();
    return false;
  }
  
  return true;
};

// Login function
export const login = async (username, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json();

    if (data.success) {
      storeAuthData(data.token, data.username, data.expiresAt);
      console.log('✅ Login successful');
      return { success: true, user: { username: data.username } };
    } else {
      console.log('❌ Login failed:', data.error);
      return { success: false, error: data.error };
    }
  } catch (error) {
    console.error('❌ Login error:', error);
    return { success: false, error: 'Network error' };
  }
};

// Logout function
export const logout = async () => {
  try {
    const token = getStoredToken();
    
    if (token) {
      // Call backend logout endpoint
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    }
  } catch (error) {
    console.error('❌ Logout error:', error);
  } finally {
    // Always clear local storage
    clearAuthData();
    console.log('✅ Logged out');
  }
};

// Verify token with backend
export const verifyToken = async () => {
  try {
    const token = getStoredToken();
    
    if (!token || isTokenExpired()) {
      clearAuthData();
      return { valid: false, error: 'No valid token' };
    }

    const response = await fetch(`${API_BASE_URL}/auth/verify`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (data.valid) {
      // Update expiration time if token is still valid
      localStorage.setItem(EXPIRES_KEY, data.expiresAt);
      return { valid: true, user: { username: data.username } };
    } else {
      clearAuthData();
      return { valid: false, error: data.error };
    }
  } catch (error) {
    console.error('❌ Token verification error:', error);
    clearAuthData();
    return { valid: false, error: 'Verification failed' };
  }
};

// Add authorization header to fetch requests
export const authenticatedFetch = async (url, options = {}) => {
  const token = getStoredToken();
  
  if (!token || isTokenExpired()) {
    throw new Error('Not authenticated');
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // If unauthorized, clear auth data
  if (response.status === 401) {
    clearAuthData();
    throw new Error('Authentication expired');
  }

  return response;
};

// Get formatted time until token expires
export const getTimeUntilExpiry = () => {
  const expiresAt = localStorage.getItem(EXPIRES_KEY);
  if (!expiresAt) return null;
  
  const now = new Date();
  const expires = new Date(expiresAt);
  const diff = expires.getTime() - now.getTime();
  
  if (diff <= 0) return 'Expired';
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  return `${hours}h ${minutes}m`;
}; 